// src/store/useAuthStore.ts
import {create} from "zustand";
import {
  getAuthToken,
  logout,
  login,
  loginWithProvider as loginWithProviderService,
  refreshToken
} from "../services/authService";
import {setAuthToken, setOnUnauthorized} from "../services/httpService";
import useUserStore from "./useUserStore.ts";
import {fetchUser} from "../services/userService.ts";
import type {IUser} from "../models/interfaces/user.interface.ts";
import useWorkspaceStore from "./useWorkspaceStrore.ts";
import {
  getGoogleAuthCode,
  startMicrosoftRedirect,
} from "../services/oauthProviders.ts";
import type {OAuthProvider} from "../services/oauthProviders.ts";
import {providerLoginErrorMessage} from "../utils/loginErrors.ts";
import {useCreditsStore} from "./useCreditsStore.ts";

interface IAuthState {
  isAuthenticated: boolean;
  error: string | null;
  loading: boolean;
  activeProvider: OAuthProvider | null;
  initializeAuth: () => Promise<void>; // Auto-login function
  login: (username: string, password: string) => Promise<void>;
  completeProviderLogin: (provider: OAuthProvider, credentialPromise: Promise<string | null>) => Promise<void>;
  loginWithProvider: (provider: OAuthProvider) => Promise<void>;
  refreshToken: () => Promise<void>;
  logout: () => void;
}

const useAuthStore = create<IAuthState>((set, get) => {
  const hydrateCredits = async (workspaceId: string): Promise<void> => {
    useCreditsStore.getState().bindWorkspace(workspaceId);
    await Promise.all([
      useCreditsStore.getState().fetchBalance(workspaceId),
      useCreditsStore.getState().fetchCatalog(),
    ]);
  };

  const completeLogin = async (user: IUser): Promise<void> => {
    set({isAuthenticated: true, error: null});
    useUserStore.getState().initUser(user);
    await hydrateCredits(user.workspaceId);
    useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
  };

  return {
  isAuthenticated: false,
  error: null,
  loading: true, // Add a loading state to track whether the app is trying to auto-login
  activeProvider: null,

  // Initialize auth (check if the user is logged in on app load)
  initializeAuth: async () => {
    const token: string | null = getAuthToken();
    if (token) {
      try {
        setAuthToken(token);
        const user: IUser = await fetchUser();
        set({isAuthenticated: true, error: null});
        useUserStore.getState().initUser(user); // initialize the user store with the user data
        await hydrateCredits(user.workspaceId);
        useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
      } catch (err: unknown) {
        console.error("Auto-login failed:", err);
        set({error: "Something went wrong"});
        logout();
      } finally {
        set({loading: false});
      }
    } else {
      set({loading: false});
    }
  },

  // Login function using email/password
  login: async (username, password) => {
    try {
      set({loading: true});
      const {user} = await login(username, password);
      await completeLogin(user);
    } catch {
      set({error: "Login failed. Please check your credentials."});
    } finally {
      set({loading: false});
    }
  },

  completeProviderLogin: async (provider, credentialPromise) => {
    try {
      set({loading: true, activeProvider: provider, error: null});
      const credential = await credentialPromise;
      if (!credential) return;

      const {user} = await loginWithProviderService(provider, credential);
      await completeLogin(user);
    } catch (err: unknown) {
      set({error: providerLoginErrorMessage(provider, err)});
    } finally {
      set({loading: false, activeProvider: null});
    }
  },

  loginWithProvider: (provider) => {
    if (provider === "google") {
      return get().completeProviderLogin("google", getGoogleAuthCode());
    }

    return (async () => {
      set({loading: true, activeProvider: "microsoft", error: null});
      try {
        await startMicrosoftRedirect();
      } catch (err: unknown) {
        set({error: providerLoginErrorMessage("microsoft", err)});
        set({loading: false, activeProvider: null});
      }
    })();
  },

  // Refresh token function
  refreshToken: async () => {
    try {
      set({loading: true});
      const {user} = await refreshToken();
      set({isAuthenticated: true, error: null});
      useUserStore.getState().initUser(user); // initialize the user store with the user data
      await hydrateCredits(user.workspaceId);
      useWorkspaceStore.getState().getWorkspaceMembers().then(() => null);
    } catch (err: unknown) {
      console.error("Token refresh failed:", err);
      set({error: "Failed to refresh token"});
    } finally {
      set({loading: false});
    }
  },

  // Logout function
  logout: () => {
    logout();
    set({isAuthenticated: false});
    useUserStore.getState().removeUser(); // Clear user store when logged out
    useCreditsStore.getState().reset();
  },
  };
});

// When any API call comes back 401 (expired/invalid session), log out so
// ProtectedRoute redirects to /login and the polling intervals unmount.
setOnUnauthorized(() => {
  const {isAuthenticated, logout} = useAuthStore.getState();
  if (isAuthenticated) {
    logout();
  }
});

export default useAuthStore;
